import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import DashboardPage from './components/DashboardPage';
import NewWorkOrderForm from './components/NewWorkOrderForm';
import NewUstanovaForm from './components/NewUstanovaForm';
import NewAparatForm from './components/NewAparatForm';
import NewDioForm from './components/NewDioForm';
import WorkOrderDetails from './components/WorkOrderDetails';
import EditWorkOrderForm from './components/EditWorkOrderForm';
import RegisterPage from './components/RegisterPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AparatiList from './components/AparatiList';
import EditAparatForm from './components/EditAparatForm';
import UstanoveList from './components/UstanoveList';
import DijeloviList from './components/DijeloviList';
import EditUstanovaForm from './components/EditUstanovaForm';
import EditDioForm from './components/EditDioForm';
import ReportsServicesPerAparat from './components/ReportsServicesPerAparat';
import ReportsServicesPerServiser from './components/ReportsServicesPerServiser';
import ReportsServicesPerUstanova from './components/ReportsServicesPerUstanova';
import ReportsPartsPerAparat from './components/ReportsPartsPerAparat';
import ReportsConsumedParts from './components/ReportsConsumedParts';
import ReportsTimePerAparatServiser from './components/ReportsTimePerAparatServiser';
import ReportsTimePerServiser from './components/ReportsTimePerServiser';
import LowStockParts from './components/LowStockParts';
import UsersList from './components/UsersList';
import UserForm from './components/UserForm';
import ProfilePage from './components/ProfilePage';
import PasswordResetRequest from './components/PasswordResetRequest';
import PasswordReset from './components/PasswordReset';
import AuditLogsPage from './components/AuditLogsPage';
import CompanyInfoPage from './components/CompanyInfoPage';

function App() {
  // Initialize from localStorage to avoid first-render redirect
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.role || null;
    } catch {
      return null;
    }
  });

  // Keep auth and role in sync when auth state changes (e.g., login/logout)
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      setUserRole(user?.role || null);
    } catch {
      setUserRole(null);
    }
  }, [isAuthenticated]);

  // Initialize and apply theme (light/dark)
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const theme = savedTheme || (prefersDark ? 'dark' : 'light');
      document.documentElement.setAttribute('data-theme', theme);
    } catch {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  const isAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.role === 'admin';
    } catch {
      return false;
    }
  })();

  return (
    <BrowserRouter>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
        <main className="flex-1 container mx-auto px-4 py-6">
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <div className="min-h-[80vh] flex items-center justify-center">
                    <LoginPage setIsAuthenticated={setIsAuthenticated} />
                  </div>
                )
              }
            />
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <div className="min-h-[80vh] flex items-center justify-center">
                    <LoginPage setIsAuthenticated={setIsAuthenticated} />
                  </div>
                )
              }
            />
            <Route path="/forgot-password" element={<PasswordResetRequest />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  <DashboardPage setIsAuthenticated={setIsAuthenticated} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/new-work-order"
              element={isAuthenticated ? <NewWorkOrderForm /> : <Navigate to="/" />}
            />
            <Route
              path="/aparati"
              element={isAuthenticated ? <AparatiList /> : <Navigate to="/" />}
            />
            <Route
              path="/aparati/:id/edit"
              element={isAuthenticated ? <EditAparatForm /> : <Navigate to="/" />}
            />
            <Route
              path="/new-ustanova"
              element={isAuthenticated ? <NewUstanovaForm /> : <Navigate to="/" />}
            />
            <Route
              path="/ustanove"
              element={isAuthenticated ? <UstanoveList /> : <Navigate to="/" />}
            />
            <Route
              path="/ustanove/:id/edit"
              element={isAuthenticated ? <EditUstanovaForm /> : <Navigate to="/" />}
            />
            <Route
              path="/new-aparat"
              element={isAuthenticated ? <NewAparatForm /> : <Navigate to="/" />}
            />
            <Route
              path="/new-dio"
              element={isAuthenticated ? <NewDioForm /> : <Navigate to="/" />}
            />
            <Route
              path="/dijelovi"
              element={isAuthenticated ? <DijeloviList /> : <Navigate to="/" />}
            />
            <Route
              path="/dijelovi/low-stock"
              element={isAuthenticated && (userRole === 'admin' || userRole === 'finance') ? <LowStockParts /> : <Navigate to="/" />}
            />
            <Route
              path="/dijelovi/:id/edit"
              element={isAuthenticated ? <EditDioForm /> : <Navigate to="/" />}
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <ProfilePage /> : <Navigate to="/" />}
            />
            <Route
              path="/users"
              element={isAuthenticated && isAdmin ? <UsersList /> : <Navigate to="/" />}
            />
            <Route
              path="/users/new"
              element={isAuthenticated && isAdmin ? <UserForm /> : <Navigate to="/" />}
            />
            <Route path="/users/:id/edit" element={isAuthenticated && isAdmin ? <UserForm /> : <Navigate to="/" />} />
            <Route
              path="/audit-logs"
              element={isAuthenticated && isAdmin ? <AuditLogsPage /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/company"
              element={isAuthenticated && isAdmin ? <CompanyInfoPage /> : <Navigate to="/" />}
            />
            <Route
              path="/reports/services-per-aparat"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsServicesPerAparat />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/services-per-serviser"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsServicesPerServiser />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/services-per-ustanova"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsServicesPerUstanova />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/parts-per-aparat"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsPartsPerAparat />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/consumed-parts"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance' || userRole === 'user')
                  ? <ReportsConsumedParts />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/time-per-aparat-serviser"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsTimePerAparatServiser />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/reports/time-per-serviser"
              element={
                isAuthenticated && (userRole === 'admin' || userRole === 'finance')
                  ? <ReportsTimePerServiser />
                  : <Navigate to="/" />
              }
            />
            <Route
              path="/work-orders/:id"
              element={isAuthenticated ? <WorkOrderDetails /> : <Navigate to="/" />}
            />
            <Route
              path="/work-orders/:id/edit"
              element={isAuthenticated ? <EditWorkOrderForm /> : <Navigate to="/" />}
            />
            <Route path="/register" element={<RegisterPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
